/**
 * 문서 미리보기 팝업 로직 (contract / payroll / tripleheader)
 * 새 창 열기, HTML 쓰기, 이전·다음·다운로드·닫기 처리
 * activeTab별로 다른 데이터/엔드포인트를 사용
 *
 * 계약서 미리보기 시 툴바에 토글 표시:
 *   - 상용직: 개인정보제공동의서 (기본 ON)
 *   - 일용직: 3.3% 공제동의서 (기본 OFF)
 *
 * 급상여대장 미리보기 시 툴바에 탭별 토글:
 *   - 상용직/일용직/사업소득 개별 on/off (현재 탭 기본 ON)
 *
 * 토글 변경 시 postMessage → 부모 re-fetch
 */
import { useState, useRef, useEffect } from 'react';
import config from '../config';
import { buildPreviewDocument } from './documentPreviewTemplate';

const LOADING_HTML = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>미리보기</title></head><body><p>미리보기 불러오는 중...</p></body></html>';

/** template 모드: contract | payroll(급여명세서) | triple(급상여대장) */
function toTemplateMode(mode) {
  if (mode === 'payslip') return 'payroll';
  if (mode === 'triple') return 'tripleheader';
  return mode || 'contract';
}

export function useDocumentPreview({
  company,
  currentYearMonth,
  activeData,       // 현재 탭의 데이터 배열
  activeTab,        // 'fulltime' | 'day' | 'biz'
  salaryData,       // 전체 salaryData (급상여에서 여러 탭 합치기용)
  selectedRowIndex,
  downloadContractRef,
  downloadPayslipRef,
  downloadTripleRef,
  apiBase = '/api/salary',   // 문서 API 경로. 임금대장(payroll)은 '/api/payroll/docs' 를 넘긴다
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRowIndex, setPreviewRowIndex] = useState(null);
  // 계약서 부가서류 토글 상태
  const [includeConsent, setIncludeConsent] = useState(true);
  const [include33Tax, setInclude33Tax] = useState(false);
  // 급여명세서 스타일
  const [payslipStyle, setPayslipStyle] = useState('signtalkbill');
  // 급상여대장 탭별 토글 상태
  const [tripleToggles, setTripleToggles] = useState({ fulltime: true, day: false, biz: false });
  // 계약서 급여형태 + 제목 + 선택 조항
  const [wageType, setWageType] = useState('monthly');
  const [customTitle, setCustomTitle] = useState('');
  const [enabledSections, setEnabledSections] = useState(null);
  const [signingDate, setSigningDate] = useState(''); // '' = 오늘, 'YYYY-MM-DD' = 지정일
  const [redistributeBase, setRedistributeBase] = useState(true); // 월급제 급여 재계산(소수부·반올림차액을 기본급으로 이전). PDF는 항상 적용.
  const previewWindowRef = useRef(null);
  const previewHandlersRef = useRef({});
  const previewTabRef = useRef(null);

  /** 미리보기 창에 토스트 표시 (없으면 부모 alert) */
  function showToast(msg, type = 'error') {
    const win = previewWindowRef.current;
    if (win && !win.closed) {
      try {
        let container = win.document.getElementById('preview-toast-container');
        if (!container) {
          container = win.document.createElement('div');
          container.id = 'preview-toast-container';
          container.style.cssText = 'position:fixed;top:12px;right:12px;z-index:9999;display:flex;flex-direction:column;gap:8px;max-width:360px;';
          win.document.body.appendChild(container);
        }
        const toast = win.document.createElement('div');
        const bg = type === 'error' ? '#dc3545' : type === 'warn' ? '#ffc107' : type === 'loading' ? '#0d6efd' : '#198754';
        const color = type === 'warn' ? '#333' : '#fff';
        toast.style.cssText = `padding:10px 16px;border-radius:6px;background:${bg};color:${color};font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,0.2);`;
        toast.textContent = msg;
        if (type !== 'loading') {
          toast.style.cursor = 'pointer';
          toast.onclick = () => toast.remove();
          setTimeout(() => toast.remove(), 5000);
        }
        container.appendChild(toast);
        return toast;
      } catch (e) { /* cross-origin 등 실패 시 fallback */ }
    }
    if (type !== 'loading') alert(msg);
    return null;
  }

  /** 다운로드 래퍼: 로딩 토스트 표시 → 실행 → 완료/에러 토스트 */
  async function withDownloadToast(fn) {
    const loading = showToast('다운로드 준비 중...', 'loading');
    try {
      await fn();
      if (loading) loading.remove();
      showToast('다운로드 완료', 'success');
    } catch (e) {
      if (loading) loading.remove();
      showToast('다운로드 실패: ' + (e.message || '알 수 없는 오류'));
    }
  }

  /** 미리보기 창에서 파일 다운로드 트리거 (부모 창 대신 미리보기 창에서 실행하여 브라우저 다운로드 차단 방지) */
  function triggerDownload(blob, filename) {
    const win = previewWindowRef.current;
    const doc = (win && !win.closed) ? win.document : document;
    const a = doc.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    doc.body.appendChild(a);
    a.click();
    doc.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  }

  function getPreviewPdfFilename(mode, rowIndex = 0) {
    if (!company || !currentYearMonth) return 'document.pdf';
    const y = currentYearMonth.year % 100;
    const m = String(currentYearMonth.month).padStart(2, '0');
    const base = `${company.name}_${y}${m}`;
    if (mode === 'contract' && activeData?.[rowIndex]) {
      return `${base}_계약서_${activeData[rowIndex][config.CONTRACT_BASIC_NAME] || '계약서'}.pdf`;
    }
    if (mode === 'payslip' && activeData?.[rowIndex]) {
      return `${base}_급여명세서_${activeData[rowIndex][config.CONTRACT_BASIC_NAME] || '명세서'}.pdf`;
    }
    if (mode === 'triple') return `${base}_급상여대장.pdf`;
    return 'document.pdf';
  }

  function writeToPreviewWindow(htmlContent, options) {
    const win = previewWindowRef.current;
    if (!win || win.closed) return;
    const doc = win.document;
    doc.open();
    const fullOptions = options && (options.mode != null || options.rowIndex !== undefined || options.total !== undefined)
      ? { ...options, mode: toTemplateMode(options.mode || previewMode) }
      : {
          mode: toTemplateMode(previewMode),
          rowIndex: previewRowIndex ?? 0,
          total: activeData?.length ?? 0,
        };
    const fullHtml = buildPreviewDocument(htmlContent, fullOptions);
    doc.write(fullHtml);
    doc.close();
  }

  async function fetchPayslipPreviewForRow(rowIndex) {
    if (!company || !currentYearMonth || !activeData?.[rowIndex]) return;
    setPreviewLoading(true);
    try {
      const url = `${config.BACKEND_URL}${apiBase}/payslip-preview`;
      const token = localStorage.getItem('token');
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company,
          year: currentYearMonth.year,
          month: currentYearMonth.month,
          people: activeData[rowIndex],
          doc_title: activeTab === 'biz' ? '지급 명세서' : '',
          payslip_style: payslipStyle,
        }),
      });
      if (!res.ok) throw new Error('미리보기를 불러올 수 없습니다');
      const json = await res.json();
      const total = activeData?.length ?? 0;
      writeToPreviewWindow(json.html || '', {
        mode: 'payroll',
        rowIndex,
        total,
        pdfFilename: getPreviewPdfFilename('payslip', rowIndex),
        payslipStyle,
      });
    } catch (e) {
      showToast('미리보기 로드 실패: ' + (e.message || '알 수 없는 오류'));
    } finally {
      setPreviewLoading(false);
    }
  }

  function openPreviewWindow() {
    if (previewWindowRef.current && !previewWindowRef.current.closed) {
      previewWindowRef.current.focus();
      return previewWindowRef.current;
    }
    const win = window.open('', '_blank');
    if (!win) {
      alert('팝업이 차단되었을 수 있습니다. 팝업 허용 후 다시 시도해 주세요.');
      return null;
    }
    previewWindowRef.current = win;
    win.document.write(LOADING_HTML);
    return win;
  }

  const handleOpenPayslipPreview = async () => {
    if (!company || !currentYearMonth || !activeData?.length) {
      showToast('표시할 임금대장 데이터가 없습니다.', 'warn');
      return;
    }
    const rowIndex = selectedRowIndex ?? 0;
    if (!openPreviewWindow()) return;
    setPreviewMode('payslip');
    setPreviewRowIndex(rowIndex);
    setPreviewOpen(true);
    previewTabRef.current = activeTab;
    await fetchPayslipPreviewForRow(rowIndex);
  };

  const handlePayslipPreviewPrev = async () => {
    if (previewRowIndex == null || previewRowIndex <= 0) return;
    const nextIdx = previewRowIndex - 1;
    setPreviewRowIndex(nextIdx);
    await fetchPayslipPreviewForRow(nextIdx);
  };

  const handlePayslipPreviewNext = async () => {
    if (previewRowIndex == null || !activeData?.length || previewRowIndex >= activeData.length - 1) return;
    const nextIdx = previewRowIndex + 1;
    setPreviewRowIndex(nextIdx);
    await fetchPayslipPreviewForRow(nextIdx);
  };

  /** 급상여대장: 선택된 탭별로 개별 API 호출 → 각각 표를 생성하여 합침 */
  async function fetchTriplePreview(toggles) {
    if (!company || !currentYearMonth || !salaryData) return;

    const tabDefs = [
      { key: 'fulltime', label: '상용직', data: salaryData.data },
      { key: 'day',      label: '일용직', data: salaryData.day },
      { key: 'biz',      label: '사업소득', data: salaryData.biz },
    ];
    const activeTabs = tabDefs.filter(t => toggles[t.key] && t.data?.length);
    if (activeTabs.length === 0) {
      showToast('선택된 탭에 표시할 데이터가 없습니다.', 'warn');
      return;
    }

    setPreviewLoading(true);
    try {
      const url = `${config.BACKEND_URL}${apiBase}/triple-payslip-preview`;
      const token = localStorage.getItem('token');
      // 탭별로 병렬 API 호출
      const results = await Promise.all(activeTabs.map(async (tab) => {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company,
            year: currentYearMonth.year,
            month: currentYearMonth.month,
            peoples: tab.data,
            label: activeTabs.length > 1 ? tab.label : '',
          }),
        });
        if (!res.ok) throw new Error('미리보기를 불러올 수 없습니다');
        const json = await res.json();
        return json.html || '';
      }));
      // 각 표를 .page 래퍼로 감싸서 페이지 브레이크 구분
      const combinedHtml = results.map(h => `<div class="page">${h}</div>`).join('\n');
      writeToPreviewWindow(combinedHtml, {
        mode: 'tripleheader',
        pdfFilename: getPreviewPdfFilename('triple'),
        tripleToggles: toggles,
      });
    } catch (e) {
      showToast('미리보기 로드 실패: ' + (e.message || '알 수 없는 오류'));
    } finally {
      setPreviewLoading(false);
    }
  }

  const handleOpenTriplePreview = async () => {
    if (!company || !currentYearMonth) {
      showToast('표시할 임금대장 데이터가 없습니다.', 'warn');
      return;
    }
    if (!openPreviewWindow()) return;
    setPreviewMode('triple');
    setPreviewOpen(true);
    previewTabRef.current = activeTab;
    // 현재 탭 기본 ON
    const initToggles = {
      fulltime: activeTab === 'fulltime',
      day: activeTab === 'day',
      biz: activeTab === 'biz',
    };
    setTripleToggles(initToggles);
    await fetchTriplePreview(initToggles);
  };

  const handleClosePreview = () => {
    if (previewWindowRef.current && !previewWindowRef.current.closed) {
      previewWindowRef.current.close();
    }
    previewWindowRef.current = null;
    setPreviewOpen(false);
    setPreviewMode(null);
    setPreviewRowIndex(null);
    previewTabRef.current = null;
  };

  const handleDownloadWordFromPreview = async () => {
    if (previewMode === 'contract') downloadContractRef?.current?.(previewRowIndex);
    else if (previewMode === 'payslip') downloadPayslipRef?.current?.(previewRowIndex);
    else if (previewMode === 'triple') downloadTripleRef?.current?.();
  };

  /** PDF 단일 다운로드 (서버 생성) */
  const handleDownloadPdfFromPreview = () => withDownloadToast(async () => {
    if (!company || !currentYearMonth) return;
    const token = localStorage.getItem('token');
    const hdrs = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const y = currentYearMonth.year;
    const m = currentYearMonth.month;
    let res, filename;

    if (previewMode === 'payslip' && previewRowIndex != null && activeData?.[previewRowIndex]) {
      res = await fetch(`${config.BACKEND_URL}${apiBase}/generate-payslip-pdf`, {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ company, year: y, month: m, people: activeData[previewRowIndex], doc_title: activeTab === 'biz' ? '지급 명세서' : '', payslip_style: payslipStyle }),
      });
      const name = activeData[previewRowIndex][config.CONTRACT_BASIC_NAME] || '직원';
      filename = `${company.name}_${y % 100}${String(m).padStart(2,'0')}_급여명세서_${name}.pdf`;
    } else if (previewMode === 'contract' && previewRowIndex != null) {
      const tab = previewTabRef.current || 'fulltime';
      const dataArr = tab === 'day' ? salaryData?.day : salaryData?.data;
      const person = dataArr?.[previewRowIndex];
      if (!person) return;
      res = await fetch(`${config.BACKEND_URL}${apiBase}/generate-contract-pdf`, {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ company, year: y, month: m, people: person, include_consent: includeConsent, include_33_tax: include33Tax, wage_type: wageType, custom_title: customTitle, enabled_sections: enabledSections, signing_date_str: signingDate }),
      });
      const name = person[config.CONTRACT_BASIC_NAME] || '직원';
      filename = `${company.name}_${y % 100}${String(m).padStart(2,'0')}_계약서_${name}.pdf`;
    } else if (previewMode === 'triple') {
      res = await fetch(`${config.BACKEND_URL}${apiBase}/generate-triple-pdf`, {
        method: 'POST', headers: hdrs,
        body: JSON.stringify({ company, year: y, month: m, peoples: activeData }),
      });
      filename = `${company.name}_${y % 100}${String(m).padStart(2,'0')}_급상여대장.pdf`;
    } else { return; }

    if (!res.ok) throw new Error('PDF 다운로드에 실패했습니다.');
    const blob = await res.blob();
    triggerDownload(blob, filename);
  });

  /** 기간별 급여명세서 다운로드 (한 직원, 여러 달) */
  const handleDownloadPayslipRange = (format, fromStr, toStr) => withDownloadToast(async () => {
    if (!company || !currentYearMonth || previewRowIndex == null || !activeData?.[previewRowIndex]) return;
    const person = activeData[previewRowIndex];
    const dl = config.FIELD_DELIMITER;
    const peopleName = person[config.CONTRACT_BASIC_NAME] || '직원';
    const idKey = config.CONTRACT_BASIC_ID || `기초${dl}인적정보${dl}주민번호${dl}nan`;
    const peopleId = person[idKey] || '';
    const [fy, fm] = fromStr.split('-').map(Number);
    const [ty, tm] = toStr.split('-').map(Number);
    if (!fy || !fm || !ty || !tm) throw new Error('시작월/종료월을 확인해주세요.');
    if (fy * 100 + fm > ty * 100 + tm) throw new Error('시작월이 종료월보다 뒤입니다.');
    const token = localStorage.getItem('token');
    const res = await fetch(`${config.BACKEND_URL}${apiBase}/generate-payslip-range`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        company, people_name: peopleName, people_id: peopleId,
        from_year: fy, from_month: fm, to_year: ty, to_month: tm,
        doc_title: activeTab === 'biz' ? '지급 명세서' : '',
        payslip_style: payslipStyle, format: format || 'pdf',
        people_employee_id: person._employee_id || '',   // 개명·주민번호 정정이 있어도 같은 사람을 찾게
      }),
    });
    if (!res.ok) {
      // 서버가 알려 준 이유를 보여 준다(예: 생성할 급여명세서가 없습니다)
      const detail = await res.json().then((b) => b.detail).catch(() => null);
      throw new Error(`기간 다운로드에 실패했습니다.${detail ? ` ${detail}` : ''}`);
    }
    const blob = await res.blob();
    const ext = (format || 'pdf') === 'pdf' ? 'pdf' : 'zip';
    triggerDownload(blob, `${company.name}_${peopleName}_${fy}${String(fm).padStart(2,'0')}-${ty}${String(tm).padStart(2,'0')}.${ext}`);
  });

  /** 급여명세서 일괄 다운로드 (ZIP) — PDF 또는 Word */
  const handleDownloadAllPayslips = (format) => withDownloadToast(async () => {
    if (!company || !currentYearMonth || !activeData?.length) return;
    const fmt = format || 'pdf';
    const isPdf = fmt === 'pdf';
    const url = isPdf
      ? `${config.BACKEND_URL}${apiBase}/generate-all-payslips-pdf`
      : `${config.BACKEND_URL}${apiBase}/generate-all-payslips`;
    const token = localStorage.getItem('token');
    const body = { company, year: currentYearMonth.year, month: currentYearMonth.month, peoples: activeData };
    if (isPdf) { body.doc_title = activeTab === 'biz' ? '지급 명세서' : ''; body.payslip_style = payslipStyle; }
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('일괄 다운로드에 실패했습니다.');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${company.name}_${currentYearMonth.year}${String(currentYearMonth.month).padStart(2,'0')}_급여명세서.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  });

  /** 계약서 미리보기 fetch */
  async function fetchContractPreview(rowIndex, tab, consent, tax33, wt, title, sections, signDate, wageInfo, redistribute) {
    if (!company || !currentYearMonth || !activeData?.[rowIndex]) return;
    setPreviewLoading(true);
    try {
      const url = `${config.BACKEND_URL}${apiBase}/contract-preview`;
      const token = localStorage.getItem('token');
      const redistributeVal = redistribute ?? true;
      const bodyData = {
        company,
        year: currentYearMonth.year,
        month: currentYearMonth.month,
        people: activeData[rowIndex],
        contract_type: tab === 'day' ? 'day' : 'fulltime',
        wage_type: wt || 'monthly',
        custom_title: title || '',
        include_consent: consent ?? true,
        include_33_tax: tax33 ?? false,
        enabled_sections: sections || null,
        signing_date_str: signDate || '',
        redistribute_base: redistributeVal,
      };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      });
      if (!res.ok) throw new Error('미리보기를 불러올 수 없습니다');
      const json = await res.json();
      // 상용직+일용직 통합 직원 리스트
      const allEmployees = buildAllEmployees();
      const gIdx = currentGlobalIndex(tab, rowIndex);
      const currentEmp = allEmployees[gIdx] || {};

      // 감지값이 없으면 현재 직원에서 추출
      const dataArr = tab === 'day' ? salaryData?.day : salaryData?.data;
      const curPerson = dataArr?.[rowIndex];
      const info = wageInfo || (curPerson ? detectWageInfo(curPerson, tab) : {});

      writeToPreviewWindow(json.html || '', {
        mode: 'contract',
        pdfFilename: getPreviewPdfFilename('contract', rowIndex),
        contractTab: tab,
        includeConsent: consent,
        include33Tax: tax33,
        wageType: wt || 'monthly',
        customTitle: title || '',
        enabledSections: sections || null,
        signingDate: signDate || '',
        redistributeBase: redistributeVal,
        employees: allEmployees,
        currentEmployeeIndex: gIdx,
        currentEmployeeName: currentEmp.name || '',
        wageValues: info.values || {},
        wageWarning: info.warning || '',
      });
    } catch (e) {
      showToast('미리보기 로드 실패: ' + (e.message || '알 수 없는 오류'));
    } finally {
      setPreviewLoading(false);
    }
  }

  /** 급여 형태 자동 감지 + 감지값 추출 */
  function detectWageInfo(person, tab) {
    const dl = config.FIELD_DELIMITER;
    const getVal = (key) => {
      const v = person[`계약서${dl}근로조건${dl}${key}${dl}nan`] || person[`계약서${dl}근로조건${dl}${key}`];
      return (v !== '' && v !== null && v !== undefined && v !== 'None' && v !== 0) ? v : null;
    };
    const yeonbong = getVal('연봉');
    const hourly = getVal('시급');
    const daily = getVal('일급');
    const netPay = getVal('네트');

    let wt = 'monthly';
    let warning = '';
    if (tab === 'fulltime' || tab === 'biz') {
      const count = [yeonbong, hourly, daily].filter(v => v != null).length;
      if (count > 1) warning = '연봉/시급/일급 중 하나만 입력되어야 합니다.';
      else if (count === 0) warning = '연봉/시급/일급 중 하나의 값이 필요합니다.';
      if (daily != null) wt = 'daily';
      else if (hourly != null) wt = 'hourly';
      else wt = 'monthly';
    } else if (tab === 'day') {
      const count = [hourly, daily].filter(v => v != null).length;
      if (count > 1) warning = '시급/일급 중 하나만 입력되어야 합니다.';
      else if (count === 0) warning = '시급 또는 일급 값이 필요합니다.';
      if (hourly != null) wt = 'hourly';
      else wt = 'daily';
    }
    return { wageType: wt, warning, values: { yeonbong, hourly, daily, netPay } };
  }

  /** 상용직+일용직 통합 직원 리스트 생성 */
  function buildAllEmployees() {
    const result = [];
    const nameKey = config.CONTRACT_BASIC_NAME;
    (salaryData?.data || []).forEach((p, i) => {
      result.push({ globalIndex: result.length, tab: 'fulltime', tabIndex: i, tabLabel: '상용', name: p[nameKey] || `직원 ${i + 1}` });
    });
    (salaryData?.day || []).forEach((p, i) => {
      result.push({ globalIndex: result.length, tab: 'day', tabIndex: i, tabLabel: '일용', name: p[nameKey] || `직원 ${i + 1}` });
    });
    return result;
  }

  /** 통합 직원 리스트에서 현재 직원의 globalIndex 계산 */
  function currentGlobalIndex(tab, tabIndex) {
    const ftLen = salaryData?.data?.length || 0;
    if (tab === 'day') return ftLen + tabIndex;
    return tabIndex;
  }

  /** globalIndex에서 tab + tabIndex 역산 */
  function resolveGlobalIndex(gIdx) {
    const ftLen = salaryData?.data?.length || 0;
    if (gIdx < ftLen) return { tab: 'fulltime', tabIndex: gIdx, data: salaryData?.data };
    return { tab: 'day', tabIndex: gIdx - ftLen, data: salaryData?.day };
  }

  const handleOpenContractPreview = async () => {
    if (selectedRowIndex === null || !company || !currentYearMonth || !activeData?.[selectedRowIndex]) {
      showToast('임금대장에서 대상자를 선택한 뒤 계약서 미리보기를 열어주세요.', 'warn');
      return;
    }
    if (!openPreviewWindow()) return;
    setPreviewMode('contract');
    setPreviewRowIndex(selectedRowIndex);
    setPreviewOpen(true);
    previewTabRef.current = activeTab;
    const consent = true;
    const tax33 = false;
    setIncludeConsent(consent);
    setInclude33Tax(tax33);
    const redistribute = true; // 미리보기 최초 오픈 시 재계산본(=PDF와 동일)으로 표시
    setRedistributeBase(redistribute);

    const person = activeData[selectedRowIndex];
    const info = detectWageInfo(person, activeTab);
    setWageType(info.wageType);
    setCustomTitle('');
    await fetchContractPreview(selectedRowIndex, activeTab, consent, tax33, info.wageType, '', null, '', info, redistribute);
  };

  /** 미리보기 창에서 토글 변경 시 re-fetch */
  const handleToggleConsent = async (newValue) => {
    setIncludeConsent(newValue);
    if (previewMode === 'contract' && previewRowIndex != null) {
      await fetchContractPreview(previewRowIndex, previewTabRef.current, newValue, include33Tax, wageType, customTitle, enabledSections, signingDate, undefined, redistributeBase);
    }
  };

  const handleToggle33Tax = async (newValue) => {
    setInclude33Tax(newValue);
    if (previewMode === 'contract' && previewRowIndex != null) {
      await fetchContractPreview(previewRowIndex, previewTabRef.current, includeConsent, newValue, wageType, customTitle, enabledSections, signingDate, undefined, redistributeBase);
    }
  };

  /** 급여형태 변경 시 re-fetch */
  const handleChangeWageType = async (newWageType) => {
    setWageType(newWageType);
    setCustomTitle('');
    if (previewMode === 'contract' && previewRowIndex != null) {
      await fetchContractPreview(previewRowIndex, previewTabRef.current, includeConsent, include33Tax, newWageType, '', enabledSections, signingDate, undefined, redistributeBase);
    }
  };

  /** 제목 변경 (DOM에서 직접 처리, 상태만 기록) */
  const handleChangeTitle = (newTitle) => {
    setCustomTitle(newTitle);
  };

  /** 급여명세서 스타일 변경 시 re-fetch */
  const handleChangePayslipStyle = async (newStyle) => {
    setPayslipStyle(newStyle);
    if (previewMode === 'payslip' && previewRowIndex != null) {
      // style 변경 후 바로 re-fetch (payslipStyle state가 아직 업데이트 안됐으므로 직접 전달)
      setPreviewLoading(true);
      try {
        const url = `${config.BACKEND_URL}${apiBase}/payslip-preview`;
        const token = localStorage.getItem('token');
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            company,
            year: currentYearMonth.year,
            month: currentYearMonth.month,
            people: activeData[previewRowIndex],
            doc_title: activeTab === 'biz' ? '지급 명세서' : '',
            payslip_style: newStyle,
          }),
        });
        if (!res.ok) throw new Error('미리보기를 불러올 수 없습니다');
        const json = await res.json();
        const total = activeData?.length ?? 0;
        writeToPreviewWindow(json.html || '', {
          mode: 'payroll',
          rowIndex: previewRowIndex,
          total,
          pdfFilename: getPreviewPdfFilename('payslip', previewRowIndex),
          payslipStyle: newStyle,
        });
      } catch (e) {
        showToast('미리보기 로드 실패: ' + (e.message || '알 수 없는 오류'));
      } finally {
        setPreviewLoading(false);
      }
    }
  };

  /** 작성일 변경 시 re-fetch */
  const handleChangeSigningDate = async (dateStr) => {
    setSigningDate(dateStr);
    if (previewMode === 'contract' && previewRowIndex != null) {
      await fetchContractPreview(previewRowIndex, previewTabRef.current, includeConsent, include33Tax, wageType, customTitle, enabledSections, dateStr, undefined, redistributeBase);
    }
  };

  /** 조항 선택 변경 시 re-fetch */
  const handleChangeSections = async (sectionIds) => {
    setEnabledSections(sectionIds);
    if (previewMode === 'contract' && previewRowIndex != null) {
      await fetchContractPreview(previewRowIndex, previewTabRef.current, includeConsent, include33Tax, wageType, customTitle, sectionIds, signingDate, undefined, redistributeBase);
    }
  };

  /** 직원 변경 시 re-fetch */
  const handleChangeEmployee = async (globalIndex) => {
    if (previewMode !== 'contract') return;
    const { tab, tabIndex, data } = resolveGlobalIndex(globalIndex);
    const person = data?.[tabIndex];
    if (!person) return;

    previewTabRef.current = tab;
    setPreviewRowIndex(tabIndex);
    const info = detectWageInfo(person, tab);
    setWageType(info.wageType);
    setCustomTitle('');
    await fetchContractPreview(tabIndex, tab, includeConsent, include33Tax, info.wageType, '', enabledSections, signingDate, info, redistributeBase);
  };

  const handleTripleToggle = async (tab, value) => {
    const next = { ...tripleToggles, [tab]: value };
    // 최소 하나는 선택되어야 함
    if (!next.fulltime && !next.day && !next.biz) return;
    setTripleToggles(next);
    if (previewMode === 'triple') {
      await fetchTriplePreview(next);
    }
  };

  useEffect(() => {
    previewHandlersRef.current = {
      payslipPrev: handlePayslipPreviewPrev,
      payslipNext: handlePayslipPreviewNext,
      downloadWord: handleDownloadWordFromPreview,
      toggleConsent: handleToggleConsent,
      toggle33Tax: handleToggle33Tax,
      tripleToggle: handleTripleToggle,
      changeWageType: handleChangeWageType,
      changeTitle: handleChangeTitle,
      changeEmployee: handleChangeEmployee,
      changeSections: handleChangeSections,
      changeSigningDate: handleChangeSigningDate,
      changePayslipStyle: handleChangePayslipStyle,
      downloadPdf: handleDownloadPdfFromPreview,
      downloadAllPayslips: handleDownloadAllPayslips,
      downloadPayslipRange: handleDownloadPayslipRange,
    };
  });

  useEffect(() => {
    const onMessage = (e) => {
      const t = e.data?.type;
      if (t === 'payslipPrev') previewHandlersRef.current.payslipPrev?.();
      else if (t === 'payslipNext') previewHandlersRef.current.payslipNext?.();
      else if (t === 'downloadWord') previewHandlersRef.current.downloadWord?.();
      else if (t === 'downloadPdf') previewHandlersRef.current.downloadPdf?.();
      else if (t === 'toggleConsent') previewHandlersRef.current.toggleConsent?.(e.data.value);
      else if (t === 'toggle33Tax') previewHandlersRef.current.toggle33Tax?.(e.data.value);
      else if (t === 'tripleToggle') previewHandlersRef.current.tripleToggle?.(e.data.tab, e.data.value);
      else if (t === 'changeWageType') previewHandlersRef.current.changeWageType?.(e.data.value);
      else if (t === 'changeTitle') previewHandlersRef.current.changeTitle?.(e.data.value);
      else if (t === 'changeEmployee') previewHandlersRef.current.changeEmployee?.(e.data.index);
      else if (t === 'changeSections') previewHandlersRef.current.changeSections?.(e.data.value);
      else if (t === 'changeSigningDate') previewHandlersRef.current.changeSigningDate?.(e.data.value);
      else if (t === 'changePayslipStyle') previewHandlersRef.current.changePayslipStyle?.(e.data.value);
      else if (t === 'downloadAllPayslips') previewHandlersRef.current.downloadAllPayslips?.(e.data.format);
      else if (t === 'downloadPayslipRange') previewHandlersRef.current.downloadPayslipRange?.(e.data.format, e.data.from, e.data.to);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!previewOpen) return;
    const tid = setInterval(() => {
      if (previewWindowRef.current?.closed) {
        previewWindowRef.current = null;
        setPreviewOpen(false);
        setPreviewMode(null);
        setPreviewRowIndex(null);
        previewTabRef.current = null;
      }
    }, 500);
    return () => clearInterval(tid);
  }, [previewOpen]);

  return {
    previewOpen,
    previewMode,
    previewRowIndex,
    previewLoading,
    handleOpenPayslipPreview,
    handleOpenContractPreview,
    handleOpenTriplePreview,
    handleClosePreview,
  };
}
